import { RedemptionType } from '@/api/model/redemptionType';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { useListProviders } from '@/api/voucher-providers/voucher-providers';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ImageUrlsField } from '@/components/ui/image-urls-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import React from 'react';
import type { UseFormReturn } from 'react-hook-form';

export interface VoucherFormData {
  title: string;
  provider_id: string;
  description?: string;
  conditions?: string;
  value: number;
  valid_from: string;
  valid_until: string;
  max_redemptions?: number;
  status: VoucherStatus;
  redemption_type: RedemptionType;
  images?: string[];
}

interface VoucherFormProps {
  form: UseFormReturn<VoucherFormData>;
  onSubmit: (data: VoucherFormData) => void;
  isSubmitting?: boolean;
  submitText?: string;
  onCancel?: () => void;
  cancelText?: string;
  showImagesField?: boolean;
}

export const VoucherForm: React.FC<VoucherFormProps> = ({
  form,
  onSubmit,
  isSubmitting = false,
  submitText = 'Speichern',
  onCancel,
  cancelText = 'Abbrechen',
  showImagesField = true,
}) => {
  const { data: providersData, isLoading: providersLoading } = useListProviders();

  const providers = providersData?.items || [];
  const maxRedemptionsValue = form.watch('max_redemptions');
  const isUnlimited = maxRedemptionsValue === undefined;
  const lastMaxRedemptionsValueRef = React.useRef<number | undefined>(maxRedemptionsValue);

  React.useEffect(() => {
    if (maxRedemptionsValue !== undefined) {
      lastMaxRedemptionsValueRef.current = maxRedemptionsValue;
    }
  }, [maxRedemptionsValue]);

  const handleUnlimitedToggle = (checked: boolean) => {
    const isChecked = checked === true;
    if (isChecked) {
      form.setValue('max_redemptions', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      const fallbackValue = lastMaxRedemptionsValueRef.current ?? 1;
      form.setValue('max_redemptions', fallbackValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <Card as='section'>
          <CardHeader>
            <CardTitle>Grundinformationen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                control={form.control}
                name='title'
                rules={{
                  required: 'Titel ist erforderlich',
                  maxLength: {
                    value: 255,
                    message: 'Titel muss kürzer als 255 Zeichen sein',
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                      Titel<span className='text-swd-tuerkis'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='Gutscheintitel eingeben' className='h-9 rounded-xl' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='provider_id'
                rules={{ required: 'Anbieter ist erforderlich' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                      Anbieter<p className='text-swd-tuerkis'>*</p>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='h-9 rounded-xl'>
                          <SelectValue placeholder='Anbieter auswählen' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {providersLoading ? (
                          <SelectItem value='loading' disabled>
                            Anbieter werden geladen...
                          </SelectItem>
                        ) : providers.length === 0 ? (
                          <SelectItem value='no-providers' disabled>
                            Keine Anbieter verfügbar
                          </SelectItem>
                        ) : (
                          providers.map(provider => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {provider.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Beschreibung</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Detaillierte Beschreibung, was dieser Gutschein bietet'
                      className='min-h-40 rounded-xl'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='conditions'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Allgemeine Bedingungen</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Bedingungen für die Nutzung dieses Gutscheins'
                      className='min-h-40 rounded-xl'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gutscheindetails</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                required
                control={form.control}
                name='value'
                rules={{
                  required: 'Wert ist erforderlich',
                  min: { value: 0.01, message: 'Wert muss größer als 0 sein' },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wert des Gutscheins</FormLabel>
                    <FormControl>
                      <Input
                        type='number'
                        step='0.01'
                        min='0.01'
                        placeholder='0.00'
                        className='h-9 rounded-xl'
                        {...field}
                        onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                required
                control={form.control}
                name='valid_from'
                rules={{ required: 'Startdatum ist erforderlich' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gültig ab</FormLabel>
                    <FormControl>
                      <Input type='datetime-local' className='h-9 rounded-xl' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                required
                control={form.control}
                name='valid_until'
                rules={{ required: 'Enddatum ist erforderlich' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gültig bis</FormLabel>
                    <FormControl>
                      <Input type='datetime-local' className='h-9 rounded-xl' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                control={form.control}
                name='max_redemptions'
                rules={{
                  validate: value => value === undefined || value >= 1 || 'Mindestens 1',
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximale Einlösungen</FormLabel>
                    <FormControl>
                      <div className='space-y-2'>
                        <Input
                          type='number'
                          min='1'
                          placeholder='Unbegrenzt'
                          className='h-9 rounded-xl'
                          disabled={isUnlimited}
                          {...field}
                          value={field.value !== undefined && !isUnlimited ? field.value : ''}
                          onChange={e => {
                            const nextValue = e.target.value;
                            field.onChange(nextValue === '' ? undefined : parseInt(nextValue, 10));
                          }}
                        />
                        <div className='flex items-center gap-2'>
                          <Checkbox
                            id='max-redemptions-unlimited'
                            checked={isUnlimited}
                            onCheckedChange={handleUnlimitedToggle}
                          />
                          <Label htmlFor='max-redemptions-unlimited'>Unbegrenzt</Label>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='redemption_type'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Einlösemethode</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='h-9 rounded-xl'>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={RedemptionType.CODE}>Nur Code</SelectItem>
                        <SelectItem value={RedemptionType.QR_CODE}>QR-Code</SelectItem>
                        <SelectItem value={RedemptionType.BARCODE}>Barcode</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
        {showImagesField && (
          <ImageUrlsField
            control={form.control}
            name='images'
            label='Bild (max. 5 Bilder)'
            description='Bild-URLs für diesen Gutschein hinzufügen'
            maxImages={5}
          />
        )}

        <div className='flex gap-6 items-center justify-end'>
          {onCancel && (
            <Button type='button' variant='outline' size='sm' onClick={onCancel}>
              {cancelText}
            </Button>
          )}
          <Button
            type='submit'
            size='sm'
            disabled={isSubmitting}
            className='bg-swd-petrol-grau text-white hover:bg-swd-petrol-grau/90'
          >
            {isSubmitting ? 'Wird verarbeitet...' : submitText}
          </Button>
        </div>
      </form>
    </Form>
  );
};
